import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Services.css';

/* ════════════════════════════════════════════════════════════
   Flat-rate services menu
   Customer browses categories, selects multiple tiles, and
   clicks "Schedule Service" → lands on /book with the selected
   services pre-filled in the issue field + total auto-calculated.
   ════════════════════════════════════════════════════════════ */

const SERVICES = [
  // ── CARPENTRY & DOORS ──
  { id: 'c1', category: 'Carpentry & Doors', name: "Fix a sticky door", price: 75 },
  { id: 'c2', category: 'Carpentry & Doors', name: "Replace door knob or lock", price: 50 },
  { id: 'c3', category: 'Carpentry & Doors', name: "Install deadbolt", price: 75, note: 'You provide the deadbolt' },
  { id: 'c4', category: 'Carpentry & Doors', name: "Reattach loose handrail", price: 75 },
  { id: 'c5', category: 'Carpentry & Doors', name: "Repair cabinet door or drawer", price: 100 },
  { id: 'c6', category: 'Carpentry & Doors', name: "Remove & install new interior door (pre-hung)", price: 400, note: 'Existing frame, you provide the door' },
  { id: 'c7', category: 'Carpentry & Doors', name: "Replace interior door slab only", price: 150, note: 'Reuse existing hinges' },
  { id: 'c9', category: 'Carpentry & Doors', name: "Install crown molding", price: 100, note: 'Per 10 ft, single straight run' },
  { id: 'c10', category: 'Carpentry & Doors', name: "Install baseboard", price: 75, note: 'Per 10 ft, you provide the material' },
  { id: 'c12', category: 'Carpentry & Doors', name: "Replace door hinges", price: 50, note: 'Per door' },
  { id: 'c13', category: 'Carpentry & Doors', name: "Install door sweep / threshold", price: 50, note: 'You provide the part' },
  { id: 'c14', category: 'Carpentry & Doors', name: "Install bifold closet doors", price: 400, note: 'You provide the doors' },
  { id: 'c15', category: 'Carpentry & Doors', name: "Adjust kitchen cabinet doors", price: 75, note: 'Per kitchen, alignment + soft close check' },
  { id: 'c16', category: 'Carpentry & Doors', name: "Replace cabinet pulls / knobs", price: 150, note: 'Up to 20, you provide hardware' },
  { id: 'c17', category: 'Carpentry & Doors', name: "Install pet door in existing door", price: 300, note: 'Wood door only — others quoted custom' },
  { id: 'c18', category: 'Carpentry & Doors', name: "Repair drawer slides", price: 75, note: 'Per drawer' },
  { id: 'c19', category: 'Carpentry & Doors', name: "Install soft-close hinges on cabinet doors", price: 50, note: 'Per cabinet' },

  // ── DRYWALL & PAINT ──
  { id: 'd1', category: 'Drywall & Paint', name: "Patch small drywall hole (under 4\")", price: 200 },
  { id: 'd2', category: 'Drywall & Paint', name: "Patch medium drywall hole (4–12\")", price: 400 },
  { id: 'd3', category: 'Drywall & Paint', name: "Touch-up paint, small area", price: 150, note: 'Under 25 sq ft' },
  { id: 'd4', category: 'Drywall & Paint', name: "Paint 1 room (up to 100 sq ft)", price: 400, note: 'Walls only, you provide paint' },
  { id: 'd5', category: 'Drywall & Paint', name: "Patch large drywall hole (12–24\")", price: 600, note: 'Includes texture match' },
  { id: 'd6', category: 'Drywall & Paint', name: "Paint 1 room (100–200 sq ft)", price: 600, note: 'Walls + ceiling, you provide paint' },
  { id: 'd7', category: 'Drywall & Paint', name: "Paint a single door", price: 200, note: 'Both sides, you provide paint' },
  { id: 'd8', category: 'Drywall & Paint', name: "Patch ceiling hole (under 4\")", price: 300, note: 'Patch and Paint' },
  { id: 'd9', category: 'Drywall & Paint', name: "Patch ceiling hole (4–12\")", price: 600, note: 'Patch and Paint' },
  { id: 'd10', category: 'Drywall & Paint', name: "Paint accent wall (up to 100 sq ft)", price: 200 },
  { id: 'd11', category: 'Drywall & Paint', name: "Paint accent wall (100 - 300 sq ft)", price: 400 },
  { id: 'd12', category: 'Drywall & Paint', name: "Paint ceiling (up to 100 sq ft)", price: 300 },
  { id: 'd13', category: 'Drywall & Paint', name: "Paint exterior front door", price: 200 },

  // ── MOUNTING & HANGING ──
  { id: 'm1', category: 'Mounting & Hanging', name: "Hang up to 5 picture frames / mirrors", price: 150, note: 'You provide the frames or mirrors' },
  { id: 'm2', category: 'Mounting & Hanging', name: "Mount floating shelves (up to 3)", price: 150, note: 'You provide the shelves' },
  { id: 'm3', category: 'Mounting & Hanging', name: "Install bathroom grab bars", price: 75, note: 'You provide the grab bars' },
  { id: 'm4', category: 'Mounting & Hanging', name: "Install towel bars or hooks", price: 75, note: 'You provide the hardware' },
  { id: 'm5', category: 'Mounting & Hanging', name: "Mount large / heavy mirror (40\"+)", price: 200, note: 'You provide, 2-person handling included' },
  { id: 'm6', category: 'Mounting & Hanging', name: "Install curtain rods (up to 3)", price: 75, note: 'You provide the hardware' },
  { id: 'm7', category: 'Mounting & Hanging', name: "Install closet rod + shelf", price: 200, note: 'Standard single-rod configuration' },
  { id: 'm8', category: 'Mounting & Hanging', name: "Mount gallery wall (6–10 pieces)", price: 150, note: 'You provide everything' },
  { id: 'm9', category: 'Mounting & Hanging', name: "Install kitchen wall shelves", price: 100, note: 'Per 4 ft, you provide shelves' },

  // ── PLUMBING ──
  { id: 'p1', category: 'Plumbing', name: "Replace kitchen faucet", price: 200, note: 'You provide the faucet' },
  { id: 'p2', category: 'Plumbing', name: "Replace bathroom faucet", price: 200, note: 'You provide the faucet' },
  { id: 'p3', category: 'Plumbing', name: "Replace toilet flapper / fill valve", price: 100 },
  { id: 'p4', category: 'Plumbing', name: "Replace toilet seat", price: 75 },
  { id: 'p5', category: 'Plumbing', name: "Replace garbage disposal", price: 300, note: 'Disposal not included' },
  { id: 'p6', category: 'Plumbing', name: "Replace shower head", price: 150, note: 'You provide the shower head' },
  { id: 'p7', category: 'Plumbing', name: "Snake a clogged drain", price: 150, note: 'Sink, tub, or shower' },
  { id: 'p8', category: 'Plumbing', name: "Replace P-trap under sink", price: 150 },
  { id: 'p9', category: 'Plumbing', name: "Reseat / replace toilet", price: 300, note: 'You provide the toilet, wax ring included' },
  { id: 'p10', category: 'Plumbing', name: "Install bidet attachment (non-electric)", price: 150, note: 'You provide the bidet' },
  { id: 'p11', category: 'Plumbing', name: "Replace toilet handle", price: 75 },
  { id: 'p12', category: 'Plumbing', name: "Caulk around tub or shower", price: 100 },
  { id: 'p13', category: 'Plumbing', name: "Re-caulk around sink", price: 75 },

  // ── ELECTRICAL ──
  { id: 'e1', category: 'Electrical', name: "Replace standard light fixture", price: 100, note: 'You provide the fixture' },
  { id: 'e2', category: 'Electrical', name: "Install ceiling fan (existing wiring)", price: 100, note: 'You provide the fixture' },
  { id: 'e3', category: 'Electrical', name: "Replace outlet or switch", price: 50 },
  { id: 'e4', category: 'Electrical', name: "Install dimmer switch", price: 50 },
  { id: 'e5', category: 'Electrical', name: "Mount TV (up to 65\")", price: 250, note: 'Standard mount included' },
  { id: 'e6', category: 'Electrical', name: "Mount TV (over 65\")", price: 400, note: 'Standard mount included' },
  { id: 'e7', category: 'Electrical', name: "Hang chandelier — 12 ft ceiling or lower", price: 150, note: 'You provide the fixture' },
  { id: 'e8', category: 'Electrical', name: "Hang chandelier — 13 ft ceiling or higher", price: 300, note: 'You provide the fixture' },
  { id: 'e9', category: 'Electrical', name: "Replace ceiling fan (existing fan box)", price: 150, note: 'You provide the fan' },
  { id: 'e10', category: 'Electrical', name: "Install smart thermostat", price: 100, note: 'You provide the device — Nest, Ecobee, etc.' },
  { id: 'e11', category: 'Electrical', name: "Install smart doorbell", price: 100, note: 'You provide the device' },
  { id: 'e12', category: 'Electrical', name: "Install GFCI outlet", price: 125, note: 'Bathroom / kitchen / outdoor' },
  { id: 'e13', category: 'Electrical', name: "Install USB outlet (combo)", price: 125, note: 'Standard + USB combo, you provide' },
  { id: 'e14', category: 'Electrical', name: "Replace bathroom vanity light", price: 100, note: 'You provide the fixture' },
  { id: 'e15', category: 'Electrical', name: "Install motion-sensor switch", price: 125, note: 'You provide the switch' },
  { id: 'e16', category: 'Electrical', name: "Replace hardwired smoke or CO detector", price: 150, note: 'You provide the detector' },
  { id: 'e17', category: 'Electrical', name: "Replace battery smoke or CO detector", price: 100, note: 'You provide the detector' },

  // ── APPLIANCES & FIXTURES ──
  { id: 'a1', category: 'Appliances & Fixtures', name: "Install dishwasher", price: 300, note: 'Existing rough-in, you provide the dishwasher' },
  { id: 'a2', category: 'Appliances & Fixtures', name: "Install over-the-range microwave", price: 300, note: 'You provide the microwave' },
  { id: 'a3', category: 'Appliances & Fixtures', name: "Install range hood (vented)", price: 400, note: 'Existing duct, you provide the hood' },
  { id: 'a5', category: 'Appliances & Fixtures', name: "Install under-sink water filter", price: 250, note: 'You provide the filter' },
  { id: 'a8', category: 'Appliances & Fixtures', name: "Install instant hot water dispenser", price: 300, note: 'You provide the dispenser' },
  { id: 'a10', category: 'Appliances & Fixtures', name: "Install soap dispenser at sink", price: 150, note: 'You provide the dispenser' },

  // ── OUTDOOR & EXTERIOR ──
  { id: 'o1', category: 'Outdoor & Exterior', name: "Mount outdoor light fixture", price: 100, note: 'You provide the fixture' },
  { id: 'o2', category: 'Outdoor & Exterior', name: "Caulk around windows or doors (exterior)", price: 75, note: 'Per opening' },
  { id: 'o3', category: 'Outdoor & Exterior', name: "Install motion-sensor floodlight", price: 150, note: 'You provide the light, existing box location' },
  { id: 'o4', category: 'Outdoor & Exterior', name: "Install house numbers / address sign", price: 100, note: 'You provide the sign' },
  { id: 'o5', category: 'Outdoor & Exterior', name: "Power wash patio or driveway (up to 400 sq ft)", price: 300, note: 'Up to 400 sq ft' },
  { id: 'o6', category: 'Outdoor & Exterior', name: "Power wash patio or driveway (up to 800 sq ft)", price: 600, note: 'Up to 800 sq ft' },
  { id: 'o7', category: 'Outdoor & Exterior', name: "Install or replace mailbox post / mount mailbox", price: 250, note: 'You provide the mailbox' },

];

const CATEGORY_ORDER = [
  'Carpentry & Doors',
  'Drywall & Paint',
  'Mounting & Hanging',
  'Plumbing',
  'Electrical',
  'Appliances & Fixtures',
  'Outdoor & Exterior',
];

/* ── Helpers ── */
function formatPrice(p) {
  if (p == null) return '$XXX';
  return `$${p}`;
}

function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, verticalAlign: 'middle' }}>
      <path d="M5 12h14M13 5l7 7-7 7"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

/* ── Tile row with horizontal scroll + side arrow controls (desktop only) ── */
function TileRow({ items, selectedIds, toggle, formatPrice, region }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [items]);

  const scrollByAmount = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll by about one tile-width + gap
    const amount = (el.clientWidth * 0.85) * dir;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="svc-row-wrap">
      <button
        type="button"
        className={`svc-row-arrow svc-row-arrow--left ${canScrollLeft ? '' : 'svc-row-arrow--hidden'}`}
        onClick={() => scrollByAmount(-1)}
        aria-label="Scroll left"
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <div
        className={`svc-tile-row ${canScrollRight ? 'svc-tile-row--has-overflow-right' : ''} ${canScrollLeft ? 'svc-tile-row--has-overflow-left' : ''}`}
        ref={scrollRef}
      >
        {items.map(s => {
          const selected = selectedIds.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggle(s.id)}
              className={`svc-tile ${selected ? 'svc-tile--selected' : ''}`}
              aria-pressed={selected}
            >
              {selected && (
                <span className="svc-tile-check">
                  <CheckIcon />
                </span>
              )}
              <span className="svc-tile-name">{s.name}</span>
              {s.note && <span className="svc-tile-note">{s.note}</span>}
              <span className="svc-tile-price">{formatPrice(priceForRegion(s.price, region))}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className={`svc-row-arrow svc-row-arrow--right ${canScrollRight ? '' : 'svc-row-arrow--hidden'}`}
        onClick={() => scrollByAmount(1)}
        aria-label="Scroll right"
        tabIndex={canScrollRight ? 0 : -1}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>
  );
}

/* ── Region pricing ──
   LI = Long Island (base prices). NYC = +25% uplift, rounded to nearest $15
   so numbers stay clean. ────────────────────────── */
const REGION_STORAGE_KEY = 'rh_pricing_region';

function getStoredRegion() {
  if (typeof window === 'undefined') return 'LI';
  try {
    const v = window.localStorage.getItem(REGION_STORAGE_KEY);
    return v === 'NYC' ? 'NYC' : 'LI';
  } catch {
    return 'LI';
  }
}

function setStoredRegion(r) {
  try {
    window.localStorage.setItem(REGION_STORAGE_KEY, r);
  } catch {}
}

function priceForRegion(basePrice, region) {
  if (basePrice == null) return null;
  // Region toggle is hidden — all prices use the base (Long Island) value.
  // Logic preserved for future restoration.
  return basePrice;
}

export default function Services() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromEstimate = location.state?.from === 'estimate';
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [region, setRegion] = useState(getStoredRegion());

  // Persist region selection across pages
  useEffect(() => { setStoredRegion(region); }, [region]);

  // Scroll to top on mount — React Router doesn't do this by default,
  // so without it the page inherits the prior page's scroll position.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Back handler: returns to /estimate if they came from there, else to pricing section
  const handleBack = (e) => {
    e.preventDefault();
    if (fromEstimate) {
      navigate('/estimate');
      return;
    }
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (!el) return;
      const NAV_HEIGHT = window.innerWidth <= 768 ? 110 : 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 50);
  };

  // Logo goes to top of homepage
  const goToTop = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 50);
  };

  // Back button + footer brand: go home + scroll to pricing
  const goToPricing = (e) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (!el) return;
      const NAV_HEIGHT = window.innerWidth <= 768 ? 110 : 90;
      const y = el.getBoundingClientRect().top + window.pageYOffset - NAV_HEIGHT;
      window.scrollTo({ top: y, behavior: 'instant' });
    }, 50);
  };

  const grouped = useMemo(() => {
    const g = {};
    CATEGORY_ORDER.forEach(cat => { g[cat] = []; });
    SERVICES.forEach(s => {
      if (!g[s.category]) g[s.category] = [];
      g[s.category].push(s);
    });
    return g;
  }, []);

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Region-adjusted price for any service
  const adjustedPrice = (s) => priceForRegion(s.price, region);

  const selectedServices = SERVICES.filter(s => selectedIds.has(s.id));
  const total = selectedServices.reduce((sum, s) => sum + (adjustedPrice(s) || 0), 0);
  const hasUnpriced = selectedServices.some(s => s.price == null);

  // Minimum charge enforcement
  // Universal $250 minimum (was region-dependent: LI $240 / NYC $300)
  const MIN_CHARGE = 250;
  const belowMinimum = !hasUnpriced && selectedServices.length > 0 && total < MIN_CHARGE;
  const shortfall = belowMinimum ? MIN_CHARGE - total : 0;
  const canSchedule = selectedServices.length > 0 && (hasUnpriced || total >= MIN_CHARGE);

  const handleSchedule = () => {
    if (selectedServices.length === 0) return;
    if (belowMinimum) return; // safety net — button should be disabled too
    // Build a pre-filled issue description from the selected services
    const services = selectedServices.map(s => ({
      id: s.id,
      name: s.name,
      price: adjustedPrice(s),
    }));
    const lines = services.map(s => `• ${s.name} — ${formatPrice(s.price)}`);
    const regionLabel = region === 'NYC' ? 'NYC' : 'Long Island';
    const issue = `Flat-rate services requested (${regionLabel} pricing):\n\n${lines.join('\n')}\n\nEstimated total: ${formatPrice(total)}`;

    // Pass via location state so /book can read it
    navigate('/book', {
      state: {
        prefilledIssue: issue,
        flatRateServices: services,
        flatRateTotal: total,
        region,
      },
    });
  };

  return (
    <div className="svc-page">

      {/* ── Top bar (sticky back nav) ── */}
      <header className="svc-topbar">
        <a href={fromEstimate ? '/estimate' : '/#pricing'} onClick={handleBack} className="svc-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </a>
        <a href="/" onClick={goToTop} className="svc-brand">
          <img src={require('./assets/logo2.png')} alt="Reliable Handyman" className="svc-brand-img" />
        </a>
        <div className="svc-topbar-spacer" aria-hidden="true" />
      </header>

      {/* ── Custom-project banner (always shown) ── */}
      <div className="svc-from-estimate-banner">
        <div className="svc-container svc-from-estimate-inner">
          <div className="svc-from-estimate-text">
            <strong>Don&rsquo;t see your project listed?</strong>
          </div>
          <Link to="/estimate" className="svc-from-estimate-btn">
            Request an Estimate
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}>
              <path d="M5 12h14M13 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="svc-hero">
        <div className="svc-container">
          <h1>Flat-rate services</h1>
          <p>Pick what you need. Add as many as you want. Hit Schedule Service when you're ready and we'll book a time. All prices are firm — no surprises, no markups, no trip charges.</p>
          <p className="svc-hero-min">Two-hour minimum per visit — stack jobs to reach ${MIN_CHARGE}.</p>

          {/* Region toggle — hidden for now, restore by flipping flag */}
          {false && (
            <div className="svc-region-toggle" role="tablist" aria-label="Pricing region">
              <span className="svc-region-label">Pricing for:</span>
              <button
                type="button"
                role="tab"
                aria-selected={region === 'LI'}
                className={`svc-region-pill ${region === 'LI' ? 'svc-region-pill--active' : ''}`}
                onClick={() => setRegion('LI')}
              >
                Long Island
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={region === 'NYC'}
                className={`svc-region-pill ${region === 'NYC' ? 'svc-region-pill--active' : ''}`}
                onClick={() => setRegion('NYC')}
              >
                NYC
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── Categories ── */}
      <section className="svc-categories">
        <div className="svc-container">
          {CATEGORY_ORDER.map(cat => {
            const items = grouped[cat] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat} className="svc-category">
                <h2 className="svc-category-title">{cat}</h2>
                <TileRow
                  items={items}
                  selectedIds={selectedIds}
                  toggle={toggle}
                  formatPrice={formatPrice}
                  region={region}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Bottom sticky bar with cart total + schedule ── */}
      {selectedIds.size > 0 && (
        <div className={`svc-cart ${belowMinimum ? 'svc-cart--below-min' : ''}`}>
          <div className="svc-container svc-cart-inner">
            <div className="svc-cart-summary">
              <div className="svc-cart-count">
                {selectedIds.size} service{selectedIds.size !== 1 ? 's' : ''} selected
                {belowMinimum && (
                  <span className="svc-cart-min-badge"> · Below ${MIN_CHARGE} minimum</span>
                )}
              </div>
              <div className="svc-cart-total">
                {hasUnpriced ? 'Quote on request' : `Total: ${formatPrice(total)}`}
              </div>
              {belowMinimum && (
                <div className="svc-cart-shortfall">
                  Add <strong>${shortfall}</strong> more in services to schedule
                </div>
              )}
            </div>
            <button
              className="svc-cart-cta"
              onClick={handleSchedule}
              disabled={!canSchedule}
            >
              {belowMinimum ? `Add $${shortfall} more` : 'Schedule Service'}
              {!belowMinimum && <ArrowRight />}
            </button>
          </div>
        </div>
      )}

      {/* ── Footer (matches marketing site) ── */}
      <footer className="svc-marketing-footer">
        <div className="svc-container svc-marketing-footer-inner">
          <a href="/" onClick={goToTop} className="svc-marketing-footer-brand" style={{ textDecoration: 'none' }}>
            <img src={require('./assets/logo2.png')} alt="Reliable Handyman" style={{ height: 26 }} />
          </a>
          <div className="svc-marketing-footer-contact">
            <a href="tel:+15163869923">(516) 386-9923</a>
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>
          </div>
        </div>
      </footer>

    </div>
  );
}
